import React, { useState } from "react";
import { getApiUrl } from "../helpers";
import { brandConfig } from "../brandConfig";
import "./DavesFeast.css";
const API_URL = getApiUrl();

interface FormData {
  adventure: string;
  cuisines: string[];
  welsh: string;
  proteins: string[];
  seafood: string;
  starters: string;
  sides: string;
  dessert: string[];
  drinks: string;
  dream_dish: string;
  no_go: string;
}

interface QuestionConfig {
  name: keyof FormData;
  text: string;
  hint: string;
  type: "single" | "multi";
  max?: number;
  options: string[];
}

const sections: { label: string; questions: QuestionConfig[] }[] = [
  {
    label: "Cuisine & Style",
    questions: [
      {
        name: "adventure",
        text: "How adventurous are we feeling?",
        hint: "Select one",
        type: "single",
        options: [
          "Keep it familiar — elevated versions of things I know and love",
          "Surprise me a bit — mostly familiar with a few curveballs",
          "Go for it — I want to try things I haven't had before",
        ],
      },
      {
        name: "cuisines",
        text: "Which cuisines get you excited?",
        hint: "Select up to three",
        type: "multi",
        max: 3,
        options: [
          "British / English classics done well",
          "Mediterranean — Italian, Greek, Spanish",
          "Middle Eastern — Lebanese, Turkish, Persian",
          "Asian — Thai, Japanese, Chinese, Vietnamese",
        ],
      },
      {
        name: "welsh",
        text: "Any English touches you'd love to see?",
        hint: "Select one",
        type: "single",
        options: [
          "Definitely — roast dinners, pies, Sunday roast, sticky toffee pudding",
          "A subtle nod — one or two English elements woven in",
          "Not bothered — just make the food great",
        ],
      },
    ],
  },
  {
    label: "The Main Event",
    questions: [
      {
        name: "proteins",
        text: "What proteins do you want front and centre?",
        hint: "Select up to three",
        type: "multi",
        max: 3,
        options: [
          "Lamb — roasted, slow-cooked, cutlets",
          "Beef — roast, brisket, fillet",
          "Pork — slow-roasted belly, ribs, crackling",
          "Chicken or duck",
        ],
      },
      {
        name: "seafood",
        text: "How about seafood?",
        hint: "Select one",
        type: "single",
        options: [
          "Love it — prawns, fish, the works. Make it a feature.",
          "Happy with a bit — maybe a starter or one dish",
          "Not fussed — keep the focus on meat",
          "Actually, I'd love seafood to be the star of the whole thing",
        ],
      },
    ],
  },
  {
    label: "Starters & Sides",
    questions: [
      {
        name: "starters",
        text: "Starters and nibbles — what appeals?",
        hint: "Select one",
        type: "single",
        options: [
          "Elegant canapés and bite-sized things",
          "A big grazing board — cheese, charcuterie, dips, bread",
          "A proper first course — soup, terrine, or something plated",
          "Skip the starters, go big on mains and sides",
        ],
      },
      {
        name: "sides",
        text: "Sides and salads — what do you gravitate towards?",
        hint: "Select one",
        type: "single",
        options: [
          "Roasted vegetables and potatoes all the way",
          "Fresh salads and lighter options",
          "Grains and pulses — couscous, lentils, rice pilafs",
          "A mix of hearty and fresh",
        ],
      },
    ],
  },
  {
    label: "The Sweet Stuff",
    questions: [
      {
        name: "dessert",
        text: "Dessert — what's your weakness?",
        hint: "Select up to two",
        type: "multi",
        max: 2,
        options: [
          "Something chocolatey and rich",
          "Fruity and fresh — pavlova, tarts, possets",
          "Classic English puds — bread and butter pudding, trifle, crumble",
          "Cheese board instead of (or alongside) dessert",
        ],
      },
    ],
  },
  {
    label: "Drinks",
    questions: [
      {
        name: "drinks",
        text: "What will you be drinking?",
        hint: "Select one",
        type: "single",
        options: [
          "Beer and ale — keep it cold and flowing",
          "Wine — red, white, or both",
          "Whisky or spirits — a nice dram after dinner",
          "A bit of everything",
        ],
      },
    ],
  },
];

const DavesFeast: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    adventure: "",
    cuisines: [],
    welsh: "",
    proteins: [],
    seafood: "",
    starters: "",
    sides: "",
    dessert: [],
    drinks: "",
    dream_dish: "",
    no_go: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSingleSelect = (name: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (
    name: keyof FormData,
    value: string,
    max: number
  ) => {
    setFormData((prev) => {
      const current = prev[name] as string[];
      if (current.includes(value)) {
        return { ...prev, [name]: current.filter((v) => v !== value) };
      }
      if (current.length >= max) return prev;
      return { ...prev, [name]: [...current, value] };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    // Build message from all selections
    const lines: string[] = [];
    sections.forEach((section) => {
      lines.push(`--- ${section.label} ---`);
      section.questions.forEach((q) => {
        const val = formData[q.name];
        const display = Array.isArray(val) ? val.join(", ") : val;
        lines.push(`${q.text}: ${display || "(no selection)"}`);
      });
    });
    if (formData.dream_dish) {
      lines.push(`--- Your Call ---`);
      lines.push(`Dream dish: ${formData.dream_dish}`);
    }
    if (formData.no_go) {
      lines.push(`Things to avoid: ${formData.no_go}`);
    }

    const fullMessage = lines.join("\n");

    const query =
      "mutation SubmitContactForm($input: SubmitContactFormInput!) { submitContactForm(input: $input) { id email } }";

    const variables = {
      input: {
        firstName: "Dave",
        lastName: "(Birthday Feast Form)",
        message: fullMessage,
        formType: "fund",
        submissionUrl: window.location.href,
        notificationEmails: ["tom@tommillerservices.com", "hamishrmiller@gmail.com"],
      },
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": brandConfig.tenantId,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Submit error:", err);
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
      setSubmitting(false);
    }
  };

  const isSelected = (name: keyof FormData, value: string): boolean => {
    const val = formData[name];
    if (Array.isArray(val)) return val.includes(value);
    return val === value;
  };

  return (
    <div className="feast-page">
      <div className="feast-container">
        {!submitted ? (
          <>
            <div className="feast-header">
              <div className="feast-brand">Moments Brisbane</div>
              <h1>
                Dave's 70<sup>th</sup>
                <br />
                <em>Birthday Feast</em>
              </h1>
              <div className="feast-divider">&#x2726;</div>
              <p className="intro">
                Your dinner, your rules. We're putting together a buffet to
                celebrate seventy magnificent years — help us build the perfect
                menu by telling us what you fancy.
              </p>
            </div>

            <div>
              {sections.map((section, si) => (
                <div className="feast-section" key={si}>
                  <div className="feast-section-label">{section.label}</div>

                  {section.questions.map((q) => (
                    <div className="feast-question" key={q.name}>
                      <div className="feast-question-text">{q.text}</div>
                      <div className="feast-question-hint">{q.hint}</div>
                      <div className="feast-options">
                        {q.options.map((opt) => (
                          <div
                            key={opt}
                            className={`feast-option${q.type === "multi" ? " multi" : ""}${isSelected(q.name, opt) ? " selected" : ""}`}
                            onClick={() =>
                              q.type === "single"
                                ? handleSingleSelect(q.name, opt)
                                : handleMultiSelect(q.name, opt, q.max || 99)
                            }
                          >
                            <span className="check"></span>
                            <span className="feast-option-label">{opt}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Your Call section */}
              <div className="feast-section">
                <div className="feast-section-label">Your Call</div>

                <div className="feast-question">
                  <div className="feast-question-text">
                    Is there a dish, a flavour, or a food memory you'd love to
                    see on the table?
                  </div>
                  <div className="feast-question-hint">Anything goes</div>
                  <textarea
                    className="feast-free-text"
                    placeholder="Tell us about it..."
                    value={formData.dream_dish}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dream_dish: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="feast-question">
                  <div className="feast-question-text">
                    Anything you definitely don't want?
                  </div>
                  <div className="feast-question-hint">
                    Allergies, dislikes, things you're sick of
                  </div>
                  <textarea
                    className="feast-free-text"
                    placeholder="Let us know..."
                    value={formData.no_go}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        no_go: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="feast-submit-area">
                <button
                  className="feast-submit-btn"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? "Sending..." : "Submit My Choices"}
                </button>
                <p className="feast-submit-note">
                  We'll build your feast from here.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="feast-success-message visible">
            <div className="feast-divider">&#x2726;</div>
            <h2>Thank you, Dave</h2>
            <p>
              Your choices are in. We'll craft something
              <br />
              truly special for your evening.
            </p>
            <div className="feast-divider" style={{ marginTop: 40 }}>
              &#x2726;
            </div>
          </div>
        )}
      </div>

      <div className={`feast-error-toast${showError ? " visible" : ""}`}>
        Something went wrong — please try again.
      </div>
    </div>
  );
};

export default DavesFeast;
